import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, addDoc, serverTimestamp, deleteDoc, updateDoc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './PendingEnrollments.css';


const calculateTermsOwed = (studentStartDate, termStructure) => {
  if (!termStructure || !termStructure.termDates) {
    console.log('No term structure or termDates');
    return [];
  }
  
  const startDate = new Date(studentStartDate);
  startDate.setHours(0, 0, 0, 0);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const currentYear = new Date().getFullYear();
  const termsOwed = [];
  
  for (let termNum = 1; termNum <= 4; termNum++) {
    const termData = termStructure.termDates[`term${termNum}`];
    if (!termData || !termData.start || !termData.end) {
      continue;
    }
    
    try {
      const [startMonth, startDay] = termData.start.split('-').map(Number);
      const [endMonth, endDay] = termData.end.split('-').map(Number);
      
      // Validate month/day ranges
      if (startMonth < 1 || startMonth > 12 || endMonth < 1 || endMonth > 12) {
        console.warn(`Term ${termNum}: Invalid month`);
        continue;
      }
      
      const termStart = new Date(currentYear, startMonth - 1, startDay);
      termStart.setHours(0, 0, 0, 0);
      
      const termEnd = new Date(currentYear, endMonth - 1, endDay);
      termEnd.setHours(23, 59, 59, 999);
      
      const enrolledBeforeTermEnded = startDate <= termEnd;
      const termHasStarted = today >= termStart;
      
      // Only charge if term has STARTED and student was enrolled before it ended
      if (enrolledBeforeTermEnded && termHasStarted) {
        termsOwed.push(termNum);
      }
    } catch (error) {
      console.error(`Term ${termNum}: Error parsing dates:`, error);
    }
  }
  
  return termsOwed;
};

function PendingEnrollments({ onNavigate }) {
  const [pendingEnrollments, setPendingEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [termStructures, setTermStructures] = useState([]);
  const [selectedEnrollments, setSelectedEnrollments] = useState(new Set());
  const [schoolOverrides, setSchoolOverrides] = useState({}); 
  const [startDateOverrides, setStartDateOverrides] = useState({});
  const [processing, setProcessing] = useState(false);
  
  // NEW: Activation modal state
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [schoolToActivate, setSchoolToActivate] = useState(null);
  const [activationData, setActivationData] = useState({
    lessonDay: '',
    lessonTime: '',
    adminFee: 160,
    termFee: 1100,
    instalmentFee: 400,
    schoolCode: '',
    termStructureId: '',
    packageId: 'pkg2'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadSchools(), loadPendingEnrollments(), loadTermStructures()]);
    setLoading(false);
  };

  const loadSchools = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      const q = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const schoolsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSchools(schoolsList);
    } catch (error) {
      console.error('Error loading schools:', error);
    }
  };

  const loadTermStructures = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'configurations'));
      const structures = snapshot.docs
        .filter(doc => doc.data().configType === 'termStructure')
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      setTermStructures(structures);
    } catch (error) {
      console.error('Error loading term structures:', error);
    }
  };

  const loadPendingEnrollments = async () => {
    try {
      const user = auth.currentUser;
      
      const q = query(
        collection(db, 'pendingEnrollments'),
        where('franchiseeId', '==', user.uid),
        where('status', '==', 'pending')
      );
      
      const snapshot = await getDocs(q);
      const enrollments = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          submittedAt: data.submittedAt?.toDate() || new Date()
        };
      });
      
      enrollments.sort((a, b) => b.submittedAt - a.submittedAt);
      setPendingEnrollments(enrollments);
    } catch (error) {
      console.error('Error loading pending enrollments:', error);
    }
  };

  const getSchoolForEnrollment = (enrollment) => {
    if (schoolOverrides[enrollment.id]) {
      return schools.find(s => s.id === schoolOverrides[enrollment.id]);
    }
    
    if (enrollment.schoolId) {
      return schools.find(s => s.id === enrollment.schoolId);
    }
    
    const typed = enrollment.schoolTyped?.toLowerCase() || '';
    return schools.find(s => 
      s.schoolName.toLowerCase() === typed ||
      s.schoolName.toLowerCase().includes(typed) ||
      typed.includes(s.schoolName.toLowerCase())
    );
  };

  const handleCheckboxChange = (enrollmentId) => {
    const newSelected = new Set(selectedEnrollments);
    if (newSelected.has(enrollmentId)) {
      newSelected.delete(enrollmentId);
    } else {
      newSelected.add(enrollmentId);
    }
    setSelectedEnrollments(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedEnrollments.size === pendingEnrollments.length) {
      setSelectedEnrollments(new Set());
    } else {
      setSelectedEnrollments(new Set(pendingEnrollments.map(e => e.id)));
    }
  };

  const handleSchoolChange = (enrollmentId, schoolId) => {
    setSchoolOverrides(prev => ({
      ...prev,
      [enrollmentId]: schoolId
    }));
  };

  const handleStartDateChange = (enrollmentId, date) => {
    setStartDateOverrides(prev => ({
      ...prev,
      [enrollmentId]: date
    }));
  };

  const getStartDateForEnrollment = (enrollment) => {
    if (startDateOverrides[enrollment.id]) {
      return startDateOverrides[enrollment.id];
    }
    return enrollment.submittedAt.toISOString().split('T')[0];
  };

  const generateAccountNumber = (school, studentName) => {
    if (!school || !studentName) return '';
    
    const schoolCode = school.schoolCode || 'XX';
    const nameParts = studentName.trim().split(' ').filter(part => part.length > 0);
    
    if (nameParts.length === 0) return '';
    
    const firstName = nameParts[0];
    const restOfName = nameParts.slice(1).join('');
    const surnamePrefix = restOfName.slice(0, 3);
    
    return restOfName 
      ? `${schoolCode}-${firstName}-${surnamePrefix}`
      : `${schoolCode}-${firstName}`;
  };

  const generateSchoolCode = (schoolName) => {
    if (!schoolName) return 'SCH';
    const words = schoolName.trim().split(' ');
    if (words.length >= 2) {
      return (words[0].slice(0, 2) + words[1].slice(0, 1)).toUpperCase();
    }
    return schoolName.slice(0, 3).toUpperCase();
  };

  // NEW: Check if any selected enrollment is for a prospect school
  const checkForProspectSchools = () => {
    const enrollmentsToCheck = pendingEnrollments.filter(e => selectedEnrollments.has(e.id));
    for (const enrollment of enrollmentsToCheck) {
      const school = getSchoolForEnrollment(enrollment);
      if (school && school.schoolStatus === 'prospect') {
        return school;
      }
    }
    return null;
  };

  // NEW: Activate prospect school
  const handleActivateSchool = async () => {
    if (!schoolToActivate) return;

    try {
      const schoolRef = doc(db, 'schools', schoolToActivate.id);
      
      // Get or create term structure
      let termStructureRef;
      if (activationData.termStructureId) {
        termStructureRef = doc(db, 'configurations', activationData.termStructureId);
      }
      
      // Update school to active status with full configuration
      const updateData = {
        schoolStatus: 'active',
        lessonDay: activationData.lessonDay,
        lessonTime: activationData.lessonTime,
        adminFee: parseFloat(activationData.adminFee),
        schoolCode: activationData.schoolCode || generateSchoolCode(schoolToActivate.schoolName),
        packageId: activationData.packageId,
        schoolFee: {
          termFee: parseFloat(activationData.termFee),
          instalmentFee: parseFloat(activationData.instalmentFee),
          termType: 4
        },
        instalmentType: 3,
        lessonsPerTerm: 10,
        updatedAt: new Date()
      };

      if (termStructureRef) {
        updateData.termStructureRef = termStructureRef;
        updateData.termStructureYear = new Date().getFullYear();
      }

      await updateDoc(schoolRef, updateData);
      
      await loadSchools();
      setShowActivationModal(false);
      setSchoolToActivate(null);
      
      alert(`✅ School "${schoolToActivate.schoolName}" activated successfully!`);
      
      // Continue with approval after activation
      handleApproveSelected();
    } catch (error) {
      console.error('Error activating school:', error);
      alert('❌ Error activating school. Please try again.');
    }
  };

  const initializeYearlyTracking = (school, startDate) => {
    const tracking = {};
    const termType = school.schoolFee?.termType || 4;
    const termFee = school.schoolFee?.termFee || 0;
    const adminFee = school.adminFee || 160;
    
    for (let i = 1; i <= termType; i++) {
      tracking[`term${i}`] = {
        adminFee: i === 1 ? adminFee : 0,
        dueDate: null,
        instalments: {
          invoiceDate: null,
          status: 'not_invoiced',
          termFee: termFee,
          totalDue: i === 1 ? termFee + adminFee : termFee,
          totalOwing: 0,
          totalPaid: 0
        }
      };
    }
    
    return tracking;
  };

  // FIXED VERSION of handleApproveSelected function for PendingEnrollments.js
// This replaces the existing handleApproveSelected function starting at line 287

// Add this helper function at the top of the file (after the imports)
const calculateTermsOwed = (studentStartDate, termStructure) => {
  if (!termStructure || !termStructure.termDates) {
    console.log('No term structure or termDates');
    return [];
  }
  
  const startDate = new Date(studentStartDate);
  startDate.setHours(0, 0, 0, 0);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const currentYear = new Date().getFullYear();
  const termsOwed = [];
  
  for (let termNum = 1; termNum <= 4; termNum++) {
    const termData = termStructure.termDates[`term${termNum}`];
    if (!termData || !termData.start || !termData.end) {
      continue;
    }
    
    try {
      const [startMonth, startDay] = termData.start.split('-').map(Number);
      const [endMonth, endDay] = termData.end.split('-').map(Number);
      
      // Validate month/day ranges
      if (startMonth < 1 || startMonth > 12 || endMonth < 1 || endMonth > 12) {
        console.warn(`Term ${termNum}: Invalid month`);
        continue;
      }
      
      const termStart = new Date(currentYear, startMonth - 1, startDay);
      termStart.setHours(0, 0, 0, 0);
      
      const termEnd = new Date(currentYear, endMonth - 1, endDay);
      termEnd.setHours(23, 59, 59, 999);
      
      const enrolledBeforeTermEnded = startDate <= termEnd;
      const termHasStarted = today >= termStart;
      
      // Only charge if term has STARTED and student was enrolled before it ended
      if (enrolledBeforeTermEnded && termHasStarted) {
        termsOwed.push(termNum);
      }
    } catch (error) {
      console.error(`Term ${termNum}: Error parsing dates:`, error);
    }
  }
  
  return termsOwed;
};

// REPLACE the existing handleApproveSelected function with this one:
const handleApproveSelected = async () => {
  if (selectedEnrollments.size === 0) {
    alert('Please select at least one enrollment to approve.');
    return;
  }

  const enrollmentsToApprove = pendingEnrollments.filter(e => selectedEnrollments.has(e.id));
  const missingSchools = enrollmentsToApprove.filter(e => !getSchoolForEnrollment(e));
  
  if (missingSchools.length > 0) {
    alert(`Please select schools for: ${missingSchools.map(e => e.studentName).join(', ')}`);
    return;
  }

  // Check for prospect schools
  const prospectSchool = checkForProspectSchools();
  if (prospectSchool) {
    setSchoolToActivate(prospectSchool);
    setActivationData({
      lessonDay: '',
      lessonTime: '',
      adminFee: 160,
      termFee: 1100,
      instalmentFee: 400,
      schoolCode: generateSchoolCode(prospectSchool.schoolName),
      termStructureId: termStructures.length > 0 ? termStructures[0].id : '',
      packageId: 'pkg2'
    });
    setShowActivationModal(true);
    return;
  }

  if (!window.confirm(`Approve ${selectedEnrollments.size} enrollment(s)? This will create complete student records.`)) {
    return;
  }

  setProcessing(true);
  let successCount = 0;
  let errorCount = 0;

  try {
    const user = auth.currentUser;
    const franchiseeRef = doc(db, 'Franchisees', user.uid);

    for (const enrollment of enrollmentsToApprove) {
      try {
        const school = getSchoolForEnrollment(enrollment);
        if (!school) continue;

        const schoolRef = doc(db, 'schools', school.id);
        const startDateStr = getStartDateForEnrollment(enrollment);
        const startDate = new Date(startDateStr);
        const accountNumber = generateAccountNumber(school, enrollment.studentName);
        const yearlyTracking = initializeYearlyTracking(school, startDate);

        // ====================================================================
        // NEW: CALCULATE BALANCE BASED ON TERM STRUCTURE
        // ====================================================================
        let totalExpected = 0;
        let termsOwed = [];
        
        // Load term structure if available
        if (school.termStructureRef) {
          try {
            const termStructureDoc = await getDoc(school.termStructureRef);
            if (termStructureDoc.exists()) {
              const termStructure = termStructureDoc.data();
              
              // Calculate which terms should be charged
              termsOwed = calculateTermsOwed(startDate, termStructure);
              
              console.log(`Student: ${enrollment.studentName}`);
              console.log(`  Start date: ${startDate.toLocaleDateString()}`);
              console.log(`  Terms owed: [${termsOwed.join(', ')}]`);
            }
          } catch (error) {
            console.error('Error loading term structure:', error);
          }
        }
        
        // Calculate totalExpected
        const adminFee = school.adminFee || 160;
        const termFee = school.schoolFee?.termFee || 0;
        const totalTermFees = termsOwed.length * termFee;
        totalExpected = adminFee + totalTermFees;
        
        // Calculate balance (negative = owing, since no payments made yet)
        const totalPaid = 0; // New student, no payments yet
        const balance = -(totalExpected - totalPaid); // Will be negative
        const totalOwing = Math.abs(balance); // Positive value for display
        
        console.log(`  Admin fee: R${adminFee}`);
        console.log(`  Term fee: R${termFee}`);
        console.log(`  Total term fees: R${totalTermFees} (${termsOwed.length} terms)`);
        console.log(`  Total expected: R${totalExpected}`);
        console.log(`  Balance: R${balance}`);
        console.log(`  Total owing: R${totalOwing}`);
        // ====================================================================

        await addDoc(collection(db, 'students'), {
          franchiseeRef: franchiseeRef,
          schoolRef: schoolRef,
          schoolName: school.schoolName,
          studentName: enrollment.studentName,
          accountNumber: accountNumber,
          class: enrollment.class || '',
          dateOfBirth: enrollment.dateOfBirth || null,
          startDate: startDate,
          isActive: true,
          
          primaryContact: {
            name: enrollment.paymentResponsible,
            email: enrollment.email,
            cellPhone: enrollment.whatsapp,
            homePhone: enrollment.telephone || '',
            idNumber: '',
            relationship: '',
            workPhone: ''
          },
          primaryContactName: enrollment.paymentResponsible,
          primaryContactEmail: enrollment.email,
          primaryContactCellPhone: enrollment.whatsapp,
          primaryContactHomePhone: enrollment.telephone || '',
          
          secondaryContact: {
            name: '',
            email: '',
            cellPhone: '',
            homePhone: '',
            idNumber: '',
            relationship: '',
            workPhone: ''
          },
          
          tshirtSize: enrollment.tshirtSize,
          photoPermission: enrollment.photoPermission,
          
          feeStructure: {
            lessonsPerTerm: school.lessonsPerTerm || 8,
            paymentMethod: school.instalmentType === 3 ? 'instalment' : 'term',
            standardFee: school.schoolFee?.termFee || 0,
            terms: school.schoolFee?.termType || 4,
            instalmentsPerTerm: school.instalmentType || 3,
            lessonDay: school.lessonDay || '',
            lessonTime: school.lessonTime || ''
          },
          
          paymentMethod: school.instalmentType === 3 ? 'instalment' : 'term',
          paymentStatus: 'outstanding',
          adminFee: adminFee,
          adminFeePaid: 0, // FIXED: Should be number, not boolean
          adminFeeYear: new Date().getFullYear(),
          
          // ====================================================================
          // NEW: PROPER BALANCE FIELDS
          // ====================================================================
          balance: balance,                    // NEW: Main balance field (negative = owing)
          totalExpected: totalExpected,        // NEW: Total fees expected
          totalOwing: totalOwing,              // FIXED: Calculated value
          totalPaid: totalPaid,                // FIXED: Starts at 0
          creditBalance: 0,                    // NEW: Credit/overpayment
          lastPaymentDate: null,               // NEW: Last payment timestamp
          // ====================================================================
          
          termPayments: {
            admin: 0,
            term1: 0,
            term2: 0,
            term3: 0,
            term4: 0
          },
          
          adminFeeBreakdown: {
            total: adminFee,
            tshirtIncluded: true,
            headOfficeGets: 120,
            franchiseeKeeps: 20,
            bbContribution: 20
          },
          
          // ====================================================================
          // NEW: BRAND BUILDING FIELDS
          // ====================================================================
          bbContributionDue: 20,               // NEW: BB amount due
          bbContributionPaid: false,           // NEW: BB payment status
          bbContributionDate: null,            // NEW: BB payment date
          // ====================================================================
          
          // ====================================================================
          // NEW: INVOICE TRACKING
          // ====================================================================
          invoices: {                          // NEW: Invoice tracking
            term1: null,
            term2: null,
            term3: null,
            term4: null
          },
          paymentHistory: [],                  // NEW: Payment history array
          // ====================================================================
          
          tshirtOrdered: false,
          tShirtSize: enrollment.tshirtSize || null,
          yearlyTracking: yearlyTracking,
          
          createdAt: serverTimestamp(),
          createdDate: new Date(),
          updatedAt: new Date(),               // NEW: Update timestamp
          source: 'online-enrollment'
        });

        await deleteDoc(doc(db, 'pendingEnrollments', enrollment.id));
        successCount++;
      } catch (error) {
        console.error(`Error approving ${enrollment.studentName}:`, error);
        errorCount++;
      }
    }

    if (successCount > 0) {
      alert(`✅ Successfully approved ${successCount} enrollment(s)!${errorCount > 0 ? `\n${errorCount} failed.` : ''}`);
    } else {
      alert('❌ Failed to approve enrollments. Please try again.');
    }

    setSelectedEnrollments(new Set());
    setSchoolOverrides({});
    setStartDateOverrides({});
    await loadData();
  } catch (error) {
    console.error('Error approving enrollments:', error);
    alert('Error approving enrollments. Please try again.');
  } finally {
    setProcessing(false);
  }
};

  const handleRejectSelected = async () => {
    if (selectedEnrollments.size === 0) {
      alert('Please select at least one enrollment to reject.');
      return;
    }

    if (!window.confirm(`Reject ${selectedEnrollments.size} enrollment(s)? This will permanently delete them.`)) {
      return;
    }

    setProcessing(true);
    try {
      for (const enrollmentId of selectedEnrollments) {
        await deleteDoc(doc(db, 'pendingEnrollments', enrollmentId));
      }
      
      alert(`✅ Rejected ${selectedEnrollments.size} enrollment(s)`);
      setSelectedEnrollments(new Set());
      await loadData();
    } catch (error) {
      console.error('Error rejecting enrollments:', error);
      alert('Error rejecting enrollments');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="pending-enrollments-container">
        <div className="loading">Loading enrollments...</div>
      </div>
    );
  }

  return (
    <div className="pending-enrollments-container">
      <div className="page-header">
        <h1>💻 Online Enrollments</h1>
        <p className="subtitle">Review and approve new student enrollments from your website</p>
      </div>

      {pendingEnrollments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📬</div>
          <h2>No Pending Enrollments</h2>
          <p>When parents submit enrollments through your website, they'll appear here.</p>
        </div>
      ) : (
        <>
          <div className="table-actions">
            <div className="actions-left">
              <button 
                onClick={handleSelectAll}
                className="btn-select-all"
              >
                {selectedEnrollments.size === pendingEnrollments.length ? '☑️ Deselect All' : '☐ Select All'}
              </button>
              <span className="selected-count">
                {selectedEnrollments.size} selected
              </span>
            </div>
            <div className="actions-right">
              <button 
                onClick={handleApproveSelected}
                className="btn-approve-multiple"
                disabled={selectedEnrollments.size === 0 || processing}
              >
                ✅ Approve Selected ({selectedEnrollments.size})
              </button>
              <button 
                onClick={handleRejectSelected}
                className="btn-reject-multiple"
                disabled={selectedEnrollments.size === 0 || processing}
              >
                ❌ Reject Selected
              </button>
            </div>
          </div>

          <div className="enrollments-table-container">
            <table className="enrollments-table">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedEnrollments.size === pendingEnrollments.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>School</th>
                  <th>Student</th>
                  <th>Account #</th>
                  <th>Start Date</th>
                  <th>Email</th>
                  <th>WhatsApp</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {pendingEnrollments.map(enrollment => {
                  const matchedSchool = getSchoolForEnrollment(enrollment);
                  const needsSchoolSelection = !matchedSchool;
                  const accountNumber = matchedSchool ? generateAccountNumber(matchedSchool, enrollment.studentName) : '';
                  const startDate = getStartDateForEnrollment(enrollment);
                  const isProspect = matchedSchool?.schoolStatus === 'prospect';
                  
                  return (
                    <tr 
                      key={enrollment.id}
                      className={selectedEnrollments.has(enrollment.id) ? 'selected' : ''}
                    >
                      <td className="checkbox-col">
                        <input
                          type="checkbox"
                          checked={selectedEnrollments.has(enrollment.id)}
                          onChange={() => handleCheckboxChange(enrollment.id)}
                        />
                      </td>
                      <td className="school-col">
                        {needsSchoolSelection ? (
                          <div className="school-select-wrapper">
                            <select
                              value={schoolOverrides[enrollment.id] || ''}
                              onChange={(e) => handleSchoolChange(enrollment.id, e.target.value)}
                              className="school-select needs-selection"
                            >
                              <option value="">Select school...</option>
                              {schools.map(school => (
                                <option key={school.id} value={school.id}>
                                  {school.schoolName}
                                </option>
                              ))}
                            </select>
                            <div className="school-warning-inline">
                              ⚠️ "{enrollment.schoolTyped}"
                            </div>
                          </div>
                        ) : (
                          <div className="school-matched">
                            ✓ {matchedSchool.schoolName}
                            {isProspect && (
                              <span style={{
                                marginLeft: '8px',
                                padding: '2px 8px',
                                background: '#FEF3C7',
                                color: '#92400E',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                border: '1px solid #F59E0B'
                              }}>
                                🟡 NEW SCHOOL - NEEDS ACTIVATION
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>{enrollment.studentName}</td>
                      <td className="account-col">
                        {accountNumber ? (
                          <span className="account-preview">{accountNumber}</span>
                        ) : (
                          <span className="account-pending">-</span>
                        )}
                      </td>
                      <td className="date-input-col">
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => handleStartDateChange(enrollment.id, e.target.value)}
                          className="date-input"
                        />
                      </td>
                      <td>{enrollment.email}</td>
                      <td>{enrollment.whatsapp}</td>
                      <td className="date-col">
                        {enrollment.submittedAt.toLocaleDateString('en-ZA', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ACTIVATION MODAL */}
      {showActivationModal && schoolToActivate && (
        <div className="modal-overlay" onClick={() => {
          if (window.confirm('Cancel school activation?')) {
            setShowActivationModal(false);
            setSchoolToActivate(null);
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>🚀 Activate School</h2>
              <button onClick={() => {
                if (window.confirm('Cancel school activation?')) {
                  setShowActivationModal(false);
                  setSchoolToActivate(null);
                }
              }} className="close-btn">✕</button>
            </div>

            <div style={{ padding: '20px' }}>
              <div style={{
                background: '#FEF3C7',
                border: '2px solid #F59E0B',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <strong style={{ color: '#92400E' }}>Activating: {schoolToActivate.schoolName}</strong>
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#78350F' }}>
                  Complete the school setup to activate it and add students.
                </p>
              </div>

              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>
                    Lesson Day *
                  </label>
                  <select
                    value={activationData.lessonDay}
                    onChange={(e) => setActivationData(prev => ({ ...prev, lessonDay: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '15px'
                    }}
                  >
                    <option value="">-- Select Day --</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>
                    Lesson Time *
                  </label>
                  <input
                    type="time"
                    value={activationData.lessonTime}
                    onChange={(e) => setActivationData(prev => ({ ...prev, lessonTime: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '15px'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                      Admin Fee (R)
                    </label>
                    <input
                      type="number"
                      value={activationData.adminFee}
                      onChange={(e) => setActivationData(prev => ({ ...prev, adminFee: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #ddd',
                        borderRadius: '6px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                      Term Fee (R)
                    </label>
                    <input
                      type="number"
                      value={activationData.termFee}
                      onChange={(e) => setActivationData(prev => ({ ...prev, termFee: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #ddd',
                        borderRadius: '6px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                      Instalment (R)
                    </label>
                    <input
                      type="number"
                      value={activationData.instalmentFee}
                      onChange={(e) => setActivationData(prev => ({ ...prev, instalmentFee: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #ddd',
                        borderRadius: '6px'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>
                    School Code *
                  </label>
                  <input
                    type="text"
                    value={activationData.schoolCode}
                    onChange={(e) => setActivationData(prev => ({ ...prev, schoolCode: e.target.value.toUpperCase() }))}
                    placeholder="e.g., FUN"
                    maxLength="5"
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '15px',
                      textTransform: 'uppercase'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>
                    Term Structure
                  </label>
                  <select
                    value={activationData.termStructureId}
                    onChange={(e) => setActivationData(prev => ({ ...prev, termStructureId: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '15px'
                    }}
                  >
                    <option value="">-- Select Term Structure --</option>
                    {termStructures.map(ts => (
                      <option key={ts.id} value={ts.id}>
                        {ts.templateName || ts.configType} ({ts.termStructureYear || 'No Year'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  onClick={() => {
                    if (window.confirm('Cancel school activation?')) {
                      setShowActivationModal(false);
                      setSchoolToActivate(null);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f3f4f6',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleActivateSchool}
                  disabled={!activationData.lessonDay || !activationData.lessonTime || !activationData.schoolCode}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: activationData.lessonDay && activationData.lessonTime && activationData.schoolCode ? 'pointer' : 'not-allowed',
                    fontSize: '16px',
                    fontWeight: '600',
                    opacity: activationData.lessonDay && activationData.lessonTime && activationData.schoolCode ? 1 : 0.5
                  }}
                >
                  🚀 Activate & Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PendingEnrollments;